// TraceCanvasOverlay: a trace's canvas over whatever page is up, framed the way
// the chat console floats: the page dimmed under it, a bordered dialog, the
// trace's tab on top. Launched from anywhere a trace is known
// (openTraceCanvas). It closes from its tab, from a click on the page around
// it, or from an Esc the board did not take.
//
// `preview`: the workspace home's, for a session previewed over its panes
// (previewTraceCanvas): a workspace like the pane's, its dock and chat and
// all. Its Mount puts it in the page: it stands in the pane behind in the
// frame the preview goes, its board drawn and its chat in the home's dock,
// to work in with the sidebar beside it. It is listed in the sidebar's
// workspaces once something is put on it or its chat is asked, not for
// being mounted.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import HoverTooltip from "./HoverTooltip.jsx";
import TabStrip from "./TabStrip.jsx";
import TraceCanvas from "./TraceCanvas.jsx";
import WorkspaceDock from "./WorkspaceDock.jsx";
import { clearBoardCard, clearBoardPick, setBoardCard, setBoardPick } from "../lib/boardPickStore.js";
import { showWorkspaceInDeck } from "../lib/deckRequests.js";
import { closeTraceCanvas, closeTracePreview, mountTracePreview, traceCanvasOpen, useTraceCanvas, useTraceCanvasOpen, useTracePreview } from "../lib/traceCanvasStore.js";

const inField = (t) => !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ""));

// The workspace over the page: its board, with the dock a workspace has in
// the home's panes beside it (WorkspaceDock), its own while it is up. A cell
// picked on a board with no trace's reading behind it, and the trace
// selected on the board, read in that dock, as they do in the home's.
function OverlayWorkspace({ open, range, onRangeChange, onTitle }) {
  const id = open.id;
  const onCellPick = useCallback((pick) => setBoardPick({ ...pick, board: id }, "overlay"), [id]);
  const onCardSelect = useCallback((card, key = null) => (card ? setBoardCard({ ...card, board: id }, "overlay") : clearBoardCard(id, key, "overlay")), [id]);
  useEffect(() => () => {
    clearBoardPick(id, "overlay");
    clearBoardCard(id, null, "overlay");
  }, [id]);
  return (
    <WorkspaceDock scope="overlay" chatFor={id}>
      <TraceCanvas
        key={id}
        canvasId={id}
        trace={open.trace}
        focus={open.focus || null}
        range={range}
        onRangeChange={onRangeChange}
        onTitle={onTitle}
        dockBridge
        dockFront
        onCellPick={onCellPick}
        onCardSelect={onCardSelect}
      />
    </WorkspaceDock>
  );
}

export default function TraceCanvasOverlay({ range = "1M", onRangeChange = null, preview = false }) {
  const laid = useTraceCanvasOpen();
  const previewed = useTracePreview();
  // One shown in a pane of the workspace home is the home's, never laid over
  // a page, and one previewed over its panes is the home's preview's.
  const open = preview ? previewed : laid?.inDeck || laid?.preview ? null : laid;
  const close = preview ? closeTracePreview : closeTraceCanvas;
  const { anchor } = useTraceCanvas(open?.id || null);
  const [expanded, setExpanded] = useState(false);
  // The session's own summary, for a launcher that had no title to give.
  const [named, setNamed] = useState({ id: null, title: "" });
  // A drag on the board that ends over the page around it is no click out.
  const downOnScrim = useRef(false);
  const scrimRef = useRef(null);
  // Mount: the workspace goes into the pane in the same update the preview
  // goes in, so the page is drawn once, with it there, and never between
  // the two (lib/deckRequests.js). One the pane worked in shows already is
  // that pane's again.
  const mount = () => {
    const id = mountTracePreview();
    const now = traceCanvasOpen();
    if (id && !now?.inDeck) showWorkspaceInDeck(id, { trace: now?.trace || null, focus: now?.focus || null });
  };
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || inField(e.target)) return;
      // The board and its sheet take Esc first, to let go of what is open on
      // them; only an Esc neither took closes the canvas.
      setTimeout(() => { if (!e.defaultPrevented) close(); }, 0);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);
  if (!open || typeof document === "undefined") return null;

  const label = open.title || (named.id === open.id ? named.title : "") || `Session ${String(anchor || "").slice(0, 8)}`;
  const expand = (
    <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
      {preview ? (
        <HoverTooltip placement="bottom" label="Mount it in the pane behind, to work in it there">
          <button
            type="button"
            onClick={mount}
            className="flex cursor-pointer items-center whitespace-nowrap border-r border-[color:var(--line-soft)] px-[10px] text-[12px] leading-[16px] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)]"
            data-trace-preview-mount=""
          >
            Mount
          </button>
        </HoverTooltip>
      ) : null}
      <HoverTooltip placement="bottom" label={expanded ? "Restore" : "Expand"}>
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          aria-label={expanded ? "Restore the workspace" : "Expand the workspace"}
          className="inline-flex h-full w-[28px] shrink-0 cursor-pointer items-center justify-center px-[8px] text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--muted)]"
          data-trace-canvas-expand=""
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-[12px] w-[12px]">
            {expanded ? (
              <>
                <path d="M3 6 L6 6 L6 3 M6 6 L2 2" />
                <path d="M13 6 L10 6 L10 3 M10 6 L14 2" />
                <path d="M3 10 L6 10 L6 13 M6 10 L2 14" />
                <path d="M13 10 L10 10 L10 13 M10 10 L14 14" />
              </>
            ) : (
              <>
                <path d="M6 6 L2 2 M5 2 L2 2 L2 5" />
                <path d="M10 6 L14 2 M11 2 L14 2 L14 5" />
                <path d="M6 10 L2 14 M2 11 L2 14 L5 14" />
                <path d="M10 10 L14 14 M14 11 L14 14 L11 14" />
              </>
            )}
          </svg>
        </button>
      </HoverTooltip>
    </div>
  );

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={preview ? "Session preview" : "Workspace"}
      onPointerDown={(e) => { downOnScrim.current = e.target === scrimRef.current; }}
      onClick={(e) => {
        if (downOnScrim.current && e.target === scrimRef.current) close();
        downOnScrim.current = false;
      }}
      className="fixed inset-0 z-[10000] grid place-items-center justify-items-center"
      data-trace-canvas-overlay=""
      data-trace-preview={preview ? "" : undefined}
    >
      {/* The page around the canvas, dimmed: a layer of its own under the
          dialog, so pointing at it (where a click closes the canvas) is told
          apart from pointing at the canvas. Pointed at, the page fades a step
          further and the dialog's frame steps up a shade (a linked review
          row's border); the dialog follows the scrim for that. */}
      <div
        ref={scrimRef}
        aria-hidden="true"
        className="peer absolute inset-0 cursor-pointer bg-black/50 backdrop-saturate-[0.5] backdrop-contrast-[0.75] backdrop-brightness-[0.75] transition-colors hover:bg-black/60"
        data-trace-canvas-scrim=""
      />
      <div
        className={`relative grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] cursor-default overflow-hidden border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[12px] pb-[12px] shadow-lg transition-colors peer-hover:border-[color:color-mix(in_srgb,var(--muted)_55%,transparent)] ${expanded ? "h-screen w-screen" : "h-[88vh] w-[min(1680px,94vw)]"}`}
        data-trace-canvas-dialog=""
      >
        <TabStrip
          className="pt-[11px]"
          tabs={[{ id: open.id, label, active: true, onSelect: () => {}, onClose: close }]}
          actions={expand}
        />
        <div className="min-h-0 min-w-0 pt-[10px]">
          <OverlayWorkspace key={open.id} open={open} range={range} onRangeChange={onRangeChange} onTitle={(title) => setNamed({ id: open.id, title })} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
